import React, { useState } from 'react';

import { useMutation } from '@betterment/js-runtime';
import { Text } from '@betterment/samba-react';
import { Button, ErrorText, FormikTextInput, InputGroup, Label } from '@betterment/style-closet-react';
import { ErrorMessage, Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import { trackClick } from '../../features/track';
import { urlBuilder } from '../../Shared/urlBuilder';
import { InviteParams } from '../data';

interface SendInviteFormProps {
  trackingModule: string;
}

const inviteValidationSchema = Yup.object().shape({
  receivingUserEmail: Yup.string().email('Invalid email address').required('Email address required'),
});

const SendInviteForm: React.FC<React.PropsWithChildren<SendInviteFormProps>> = ({ trackingModule }) => {
  const [hasInviteSent, setInviteSent] = useState(false);
  const [postInvite] = useMutation<InviteParams>(urlBuilder.postPersonalReferralInvites());

  const onSubmitFlashMessage = () => {
    setInviteSent(true);

    setTimeout(() => {
      setInviteSent(false);
    }, 3000);
  };

  const onSubmitHandler = async (values: InviteParams, formik: FormikHelpers<InviteParams>) => {
    const response = await postInvite(values);
    if (!response.errors) {
      formik.setSubmitting(false);
      formik.resetForm();
      onSubmitFlashMessage();
      return;
    }

    formik.setErrors(response.errors.messages);
  };

  return (
    <Formik
      initialValues={{ receivingUserEmail: '' }}
      onSubmit={onSubmitHandler}
      validationSchema={inviteValidationSchema}
    >
      {({ errors, isSubmitting }) => (
        <Form>
          <InputGroup mt="8" mb="0" fullWidth={true}>
            <Label htmlFor="email">Email your friend</Label>
            <FormikTextInput
              fullWidth={true}
              name="receivingUserEmail"
              id="email"
              mb="3"
              borderRadius="0.3rem"
              placeholder="Email address"
            />
            <Button
              disabled={isSubmitting || hasInviteSent}
              type="submit"
              width="100%"
              aria-label="Send email invitation"
              onClick={() => trackClick({ module: trackingModule, name: 'SendEmailSubmission' })}
            >
              Invite
            </Button>
            <ErrorMessage component={ErrorText} name="receivingUserEmail" />
            {hasInviteSent && !errors.receivingUserEmail && (
              <Text
                color="text.success"
                css={{
                  fontSize: 'fontSize.50',
                }}
              >
                Invite sent
              </Text>
            )}
          </InputGroup>
        </Form>
      )}
    </Formik>
  );
};

export default SendInviteForm;
